
import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Clock,
} from "@phosphor-icons/react";
import { MapPin, Phone, Mail } from "lucide-react";

import Logo from "@/assets/logo-white.webp";
import footerPhoto from "@/assets/imgs/general/footer-image.jpg";
import { Facebook, Instagram, Youtube } from "@/components/socialmedia";

const exploreLinks = [
  { to: "/about", text: "About Us" },
  { to: "/services", text: "Services" },
  { to: "/sermons", text: "Sermons" },
  { to: "/events", text: "Events" },
  { to: "/donations", text: "Give" },
  { to: "/contact", text: "Contact" },
];

const legalLinks = [
  { to: "/privacy", text: "Privacy Policy" },
  { to: "/terms", text: "Terms & Conditions" },
  { to: "/social-disclaimer", text: "Social Media Disclaimer" },
  { to: "/safeguarding", text: "Safeguarding" },
  { to: "/gdpr", text: "GDPR" },
];

const headingFont = {
  fontFamily: "'Montserrat', sans-serif",
};

const Footer = () => {
  const reduceMotion = useReducedMotion();

  const reveal = (delay = 0) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 32 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: {
      duration: 0.8,
      delay,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  });

  return (
    <footer className="relative isolate overflow-hidden bg-black text-white">
      {/* Full-footer photographic background */}
      <img
        src={footerPhoto}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 -z-30 h-full w-full object-cover object-center sm:object-[30%_center] lg:object-[30%_center]"
      />

      {/* TOP: Welcome and visit invitation */}
      <div className="container relative z-10 mx-auto px-4">
        <div className="relative pb-24 pt-10 md:pb-32 md:pt-14">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 -inset-x-[50vw] -z-10 bg-primary brightness-50"
            style={{
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 45%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0) 100%)",
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 45%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0) 100%)",
            }}
          />

          <motion.div
            {...reveal()}
            className="flex flex-col items-center gap-8 text-center lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:text-left"
          >
            <div>
              <p
                style={headingFont}
                className="mb-3 flex items-center justify-center gap-2.5 text-base font-bold tracking-wide text-white sm:text-lg lg:justify-start"
              >
                <Clock size={22} weight="bold" aria-hidden="true" />
                Sunday worship &middot; 10:30 AM
              </p>

              <h2
                style={headingFont}
                className="text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:whitespace-nowrap lg:text-5xl xl:text-6xl"
              >
                There&rsquo;s a place for you
              </h2>

              <p className="mt-3 text-sm text-white/80 sm:text-base lg:whitespace-nowrap">
                Searching, visiting, or looking for a church home? You&rsquo;re
                always welcome.
              </p>
            </div>

            <Link
              to="/join"
              className="group relative inline-flex min-h-12 shrink-0 items-center justify-center gap-2.5 rounded-full bg-white px-8 py-3.5 text-sm font-bold text-neutral-950 shadow-xl transition-all duration-300 hover:scale-[1.02] hover:bg-neutral-100 hover:shadow-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black active:scale-[0.98]"
            >
              Plan Your Visit
              <ArrowUpRight
                size={18}
                weight="bold"
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </Link>
          </motion.div>
        </div>

        {/* Photographic window between the welcome and footer card */}
        <div
          aria-hidden="true"
          className="h-16 sm:h-32 lg:h-48"
        />

        {/* BOTTOM: Footer information */}
        <div className="relative pt-24 md:pt-32">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 -inset-x-[50vw] -z-10 bg-primary brightness-50"
            style={{
              WebkitMaskImage:
                "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 55%, rgba(0,0,0,0.6) 80%, rgba(0,0,0,0) 100%)",
              maskImage:
                "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 55%, rgba(0,0,0,0.6) 80%, rgba(0,0,0,0) 100%)",
            }}
          />

          {/* Main footer card */}
          <motion.div
            {...reveal(0.12)}
            className="grid gap-12 rounded-3xl border border-white/20 bg-primary/30 p-8 shadow-2xl backdrop-blur-xl transition-colors duration-500 hover:border-white/30 sm:p-12 lg:grid-cols-[1.1fr_1.3fr_auto] lg:items-start"
          >
            {/* Brand */}
            <div className="flex flex-col items-start">
              <Link
                to="/"
                aria-label="PIWC Leicester home"
                className="group inline-block transition-transform duration-300 hover:scale-[1.02]"
              >
                <img
                  src={Logo}
                  alt="PIWC Leicester"
                  className="h-12 w-auto sm:h-14"
                />
              </Link>

              <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/80">
                A vibrant, multicultural community of faith, hope, and love in
                the heart of Leicester.
              </p>

              <div className="mt-6 flex items-center gap-3">
                {[
                  { component: Facebook, label: "Facebook" },
                  { component: Instagram, label: "Instagram" },
                  { component: Youtube, label: "YouTube" },
                ].map(({ component: SocialIcon, label }) => (
                  <div
                    key={label}
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-white/5 text-white/80 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/15 hover:text-white"
                  >
                    <SocialIcon className="h-4 w-4 fill-current" />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation and contact */}
            <div className="grid gap-10 sm:grid-cols-2">
              <nav aria-label="Footer Navigation">
                <h3
                  style={headingFont}
                  className="mb-5 text-xs font-extrabold uppercase tracking-[0.25em] text-white/60"
                >
                  Explore
                </h3>

                <ul className="space-y-3">
                  {exploreLinks.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="group inline-flex items-center gap-2 text-sm font-medium text-white/85 transition-colors duration-200 hover:text-white focus-visible:text-white focus-visible:outline-none"
                      >
                        <span className="transition-transform duration-300 group-hover:translate-x-0.5">
                          {link.text}
                        </span>

                        <ArrowRight
                          size={14}
                          weight="bold"
                          aria-hidden="true"
                          className="-translate-x-2 opacity-0 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <div>
                <h3
                  style={headingFont}
                  className="mb-5 text-xs font-extrabold uppercase tracking-[0.25em] text-white/60"
                >
                  Find Us
                </h3>

                <ul className="space-y-4 text-sm text-white/85">
                  <li className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5">
                      <MapPin className="h-3.5 w-3.5 text-white/90" />
                    </div>
                    <span className="leading-snug">
                      Kildare Street, Leicester, LE1 3FY
                    </span>
                  </li>

                  <li className="flex items-center gap-3">
                    <div className="flex h-7 w-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5">
                      <Phone className="h-3.5 w-3.5 text-white/90" />
                    </div>
                    <a
                      href="tel:+447354849305"
                      className="transition-colors hover:text-white"
                    >
                      +44 7354 849305
                    </a>
                  </li>

                  <li className="flex items-center gap-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5">
                      <Mail className="h-3.5 w-3.5 text-white/90" />
                    </div>
                    <a
                      href="mailto:piwcleicester@copuk.org"
                      className="break-all transition-colors hover:text-white"
                    >
                      piwcleicester@copuk.org
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Giving shortcut */}
            <div className="lg:self-start">
              <Link
                to="/donations"
                className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-3 text-sm font-bold text-white transition-all duration-300 hover:border-white hover:bg-white hover:text-neutral-950 hover:shadow-lg active:scale-95"
              >
                Give
                <ArrowUpRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </Link>
            </div>
          </motion.div>

          {/* Legal information */}
          <div className="relative z-10 mt-16 border-t border-white/15">
            <div className="py-8 text-center text-white/70">
              <ul className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs font-medium sm:text-sm">
                {legalLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="transition-colors duration-200 hover:text-white"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-xs leading-relaxed sm:text-sm">
                &copy; {new Date().getFullYear()} PIWC Leicester - The Church
                of Pentecost. All Rights Reserved.
              </p>

              <p className="mx-auto mt-2 max-w-3xl text-[11px] leading-relaxed opacity-80 sm:text-xs">
                Registered in England &amp; Wales as a company limited by
                guarantee. Reg No: 6550075 | Charity No: 1123975 (England &amp;
                Wales) | Charity No: SC041252 (Scotland). |{" "}
                <a
                  href="https://www.eauk.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 transition-colors hover:text-white"
                >
                  A member of the Evangelical Alliance
                </a>
              </p>

              <p className="mt-1.5 text-[10px] leading-relaxed opacity-70 sm:text-xs">
                This site is protected by{" "}
                <a
                  href="https://www.google.com/recaptcha/about/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                >
                  reCAPTCHA
                </a>{" "}
                and the Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                >
                  Terms of Service
                </a>{" "}
                apply.
              </p>
            </div>
          </div>
                {/* Oversized typographic signature */}
      <div
        aria-hidden="true"
        className="pointer-events-none relative z-0 select-none"
      >
        <p
          style={headingFont}
          className="whitespace-nowrap text-center text-[clamp(3.5rem,15.5vw,19rem)] font-extrabold lowercase leading-[0.72] tracking-[-0.075em] text-black/15"
        >
          leicester piwc
        </p>
      </div>
        </div>
        
      </div>
    </footer>
  );
};

export default React.memo(Footer);
